import { useState } from "react";
import { Link } from "react-router-dom";
import { SUPPORT_EMAIL } from "../lib/runtimeConfig.js";
import { getTripStatus } from "../lib/travelApi.js";
import Seo from "../components/Seo.jsx";

/*
 * Track Trip — customer-facing status lookup.
 *
 * Phase 2B: wired to the live backend. The reference is validated locally,
 * then getTripStatus() (in src/lib/travelApi.js) GETs
 * https://api.513travel.com/travel/requests/{reference} and maps the
 * public-safe response to the existing UI shape (statusLabel / activeStep /
 * nextStep / createdAt). 404 -> "not found"; network/server -> friendly retry.
 */

// Accepts 513-TRIP-…, 513-QUOTE-…, 513-BOOKING-… (alphanumeric tail).
const REFERENCE_RE = /^513-[A-Z]+-[A-Z0-9]{3,}$/;

const TIMELINE = [
  { key: "received", title: "Request received", note: "We have your trip request." },
  { key: "options", title: "Options being prepared", note: "We curate flights, stays and experiences." },
  { key: "payment", title: "Confirmation & payment", note: "You confirm and pay securely." },
  { key: "support", title: "Travel support", note: "We support you before and during travel." },
];

export default function TrackTrip() {
  const [reference, setReference] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    const value = reference.trim().toUpperCase();
    setReference(value);
    if (!value) {
      setError("Please enter your request reference.");
      setResult(null);
      return;
    }
    if (!REFERENCE_RE.test(value)) {
      setError("That doesn't look right — references look like 513-TRIP-ABCD.");
      setResult(null);
      return;
    }
    setError("");
    setResult(null);
    setLoading(true);
    try {
      // getTripStatus maps the public-safe API shape to the UI's shape
      // (statusLabel / activeStep / nextStep / createdAt).
      const snapshot = await getTripStatus(value);
      setResult(snapshot);
    } catch (err) {
      if (err && err.kind === "notfound") {
        setError("We couldn't find a request with that reference. Please check it and try again.");
      } else {
        setError(
          err && err.userMessage
            ? err.userMessage
            : "We couldn't reach our system. Please try again shortly."
        );
      }
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page">
      <Seo
        title="Track Your Trip — 513Travel"
        description="Enter your 513Travel request or booking reference to follow your journey from quote to return."
        path="/track-trip"
      />
      {/* ===== Hero + lookup ===== */}
      <section className="page-hero">
        <div className="container">
          <div className="eyebrow">Track Trip</div>
          <h1>Follow your journey from quote to return</h1>
          <p className="lede">
            Enter your request or booking reference to see your trip status,
            documents, payments and next steps.
          </p>

          <div className="track-lookup">
            <form onSubmit={handleSubmit} noValidate>
              <label htmlFor="trip-ref" className="track-label">
                Request or booking reference
              </label>
              <div className="track-form-row">
                <input
                  id="trip-ref"
                  type="text"
                  value={reference}
                  onChange={(e) => {
                    setReference(e.target.value.toUpperCase());
                    if (error) setError("");
                  }}
                  placeholder="e.g. 513-TRIP-ZDF6"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck="false"
                  aria-invalid={error ? "true" : undefined}
                  aria-describedby="trip-ref-help"
                />
                <button type="submit" className="btn gold" disabled={loading}>
                  {loading ? "Checking…" : "Track Trip"}
                </button>
              </div>
              {error
                ? <p className="field-error" role="alert">{error}</p>
                : <p id="trip-ref-help" className="track-help">Your reference is shown after submitting a travel request.</p>}
            </form>
          </div>
        </div>
      </section>

      {/* ===== Result ===== */}
      {result && (
        <section className="section track-result">
          <div className="container track-grid">
            {/* Timeline + demo note */}
            <div>
              <div className="demo-note" role="status">
                We found your reference. Your current request status is shown
                below. Our team will confirm the latest update by email or WhatsApp.
              </div>

              <ol className="timeline" aria-label="Trip status timeline">
                {TIMELINE.map((step, i) => {
                  const state = i < result.activeStep
                    ? "is-done"
                    : i === result.activeStep
                      ? "is-active"
                      : "is-pending";
                  return (
                    <li key={step.key} className={`timeline-step ${state}`}>
                      <span className="t-marker" aria-hidden="true">
                        {i <= result.activeStep ? "✓" : i + 1}
                      </span>
                      <span className="t-body">
                        <span className="t-title">{step.title}</span>
                        <span className="t-note">{step.note}</span>
                      </span>
                    </li>
                  );
                })}
              </ol>
            </div>

            {/* Summary + actions */}
            <aside>
              <div className="summary-card">
                <h2>Trip summary</h2>
                <dl className="summary-list">
                  <div className="summary-row">
                    <dt>Reference</dt>
                    <dd className="summary-ref">{result.reference}</dd>
                  </div>
                  <div className="summary-row">
                    <dt>Status</dt>
                    <dd><span className="status-pill">{result.statusLabel}</span></dd>
                  </div>
                  <div className="summary-row">
                    <dt>Next step</dt>
                    <dd>{result.nextStep}</dd>
                  </div>
                  <div className="summary-row">
                    <dt>Contact</dt>
                    <dd><a className="inline-link" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></dd>
                  </div>
                  <div className="summary-row">
                    <dt>WhatsApp</dt>
                    <dd>Support available after request review</dd>
                  </div>
                </dl>
              </div>

              <div className="page-actions">
                <Link to="/plan-a-trip" className="btn gold">Plan another trip</Link>
                <Link to="/contact" className="btn outline">Contact us</Link>
              </div>
            </aside>
          </div>
        </section>
      )}
    </div>
  );
}
